<template>
  <el-dialog :visible.sync="visible" :width="width" :close-on-click-modal="false" :show-close="false"
    custom-class="order-create-dialog">
    <div class="p-4">
      <div class="flex items-center justify-between mb-2">
        <span class="text-xs font-semibold text-blue-600 bg-blue-50 rounded-full px-2 py-1">
          Paso {{ step }} de 3
        </span>
      </div>

      <h1 v-if="stepTitle" class="text-xl font-bold mb-1">{{ stepTitle }}</h1>
      <p v-if="stepSubtitle" class="text-gray-500 text-sm mb-4">
        {{ stepSubtitle }}
      </p>

      <div v-if="step === 1" class="bg-gray-50 p-3 rounded-lg mb-3 flex items-center justify-between">
        <span class="text-sm font-medium">Modo de orden</span>
        <div class="flex gap-2 items-center">
          <el-button :type="isRealOrder ? '' : 'primary'" :plain="isRealOrder" size="mini" @click="isRealOrder = false">
            ⚛️ Demo
          </el-button>
          <el-switch v-model="isRealOrder"></el-switch>
          <el-button :type="isRealOrder ? 'primary' : ''" :plain="!isRealOrder" size="mini" @click="isRealOrder = true">
            Real
          </el-button>
        </div>
      </div>

      <div v-if="step === 1" class="rounded-lg px-3 py-2 text-xs mb-4 border"
        :class="isRealOrder ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-blue-50 text-blue-700 border-blue-200'">
        <span v-if="isRealOrder">⚠️ Esta orden sera real y afectara tus registros.</span>
        <span v-else>La orden demo es solo de prueba y no afecta tus folios ni reportes.</span>
      </div>

      <el-form ref="form" :model="model" :rules="rules" label-position="top" class="mb-4">
        <div v-if="step === 1">
          <el-form-item prop="clientName">
            <label class="text-xs text-gray-500">Nombre del cliente <span class="text-red-500">*</span></label>
            <el-input v-model="model.clientName" placeholder="Juan Perez" size="small">
              <i slot="prefix" class="bx bx-user text-gray-400"></i>
            </el-input>
          </el-form-item>

          <el-form-item prop="clientPhone">
            <label class="text-xs text-gray-500">Telefono</label>
            <el-input v-model="model.clientPhone" placeholder="555 123 4567" size="small">
              <i slot="prefix" class="bx bx-phone text-gray-400"></i>
            </el-input>
          </el-form-item>

          <p class="text-xs text-gray-400">Puedes completar mas datos despues</p>
        </div>

        <div v-else-if="step === 2" class="space-y-4">
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-xs text-gray-500 font-semibold">
                Dispositivo
                <span class="text-red-500">*</span>
              </label>
              <a href="#" class="text-xs text-purple-600 hover:text-purple-700 font-semibold"
                @click.prevent="showCreateDeviceDialog = true">
                + Crear nuevo
              </a>
            </div>
            <el-select v-model="model.device" placeholder="Buscar dispositivo..." filterable remote
              :remote-method="searchDevices" :loading="deviceSearchLoading" @focus="loadSuggestedDevices"
              class="w-full">
              <i slot="prefix" class="bx bx-mobile text-gray-400"></i>
              <el-option-group label="Dispositivos sugeridos" v-if="devices.length > 0">
                <el-option v-for="device in devices" :key="device.id" :label="`${device.brand} ${device.model}`"
                  :value="device">
                </el-option>
              </el-option-group>
              <el-divider v-if="devices.length > 0" class="my-2"></el-divider>
              <el-option :value="null" disabled>
                <span class="text-sm text-gray-400">Busca otro modelo o crea uno nuevo</span>
              </el-option>
            </el-select>
          </div>

          <div>
            <label class="block text-xs text-gray-500 font-semibold mb-2">
              Describe el problema
            </label>
            <el-input v-model="model.problemDescription" type="textarea" placeholder="¿Qué le pasa al dispositivo?"
              :rows="4">
            </el-input>
          </div>

          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-xs text-gray-500 font-semibold">
                Servicio
                <span class="text-red-500">*</span>
              </label>
              <a href="#" class="text-xs text-purple-600 hover:text-purple-700 font-semibold"
                @click.prevent="showService">
                + Crear nuevo
              </a>
            </div>
            <el-select v-model="model.service" value-key="__uid" placeholder="Buscar servicio..." filterable remote
              :remote-method="searchServices" :loading="serviceSearchLoading" @focus="loadSuggestedServices"
              class="w-full">
              <i slot="prefix" class="bx bx-wrench text-gray-400"></i>
              <el-option-group label="Servicios comunes" v-if="services.length > 0">
                <el-option v-for="(service, idx) in services" :key="service.__uid || service.id || idx"
                  :label="service.name" :value="service">
                </el-option>
              </el-option-group>
              <el-divider v-if="services.length > 0" class="my-2"></el-divider>
              <el-option :value="null" disabled>
                <span class="text-sm text-gray-400"><i class="bx bx-wrench text-gray-400 mr-2"></i>Busca otro servicio o
                  crea uno nuevo</span>
              </el-option>
            </el-select>
          </div>

          <div>
            <label class="block text-xs text-gray-500 font-semibold mb-2">
              Precio final
            </label>
            <el-input v-model="model.price" type="number" placeholder="0.00">
              <span slot="prefix" style="display: flex; align-items: center; margin-right: 4px;"><i
                  class="bx bx-dollar text-gray-400" style="margin: 0;"></i></span>
            </el-input>
          </div>

          <div class="rounded-lg px-4 py-3 text-xs border mt-4"
            :class="isRealOrder ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-blue-50 text-blue-700 border-blue-200'">
            <span v-if="isRealOrder">⚠️ Esta es una orden real</span>
            <span v-else>💡 Esta es una orden de práctica</span><br>
            <span v-if="isRealOrder" class="text-yellow-600 text-xs">Afectará tus registros y folios.</span>
            <span v-else class="text-blue-600 text-xs">No afectará tu sistema real.</span>
          </div>

          <p v-if="!isRealOrder" class="text-xs text-gray-400 mt-3">Puedes editarlo despues si lo necesitas</p>
        </div>

        <div v-else class="text-center py-6">
          <div class="flex justify-center mb-6">
            <div class="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
              <i class="bx bx-check text-blue-600 text-4xl"></i>
            </div>
          </div>

          <h2 class="text-2xl font-bold mb-2">Guarda tu orden</h2>
          <p class="text-gray-600 text-sm mb-6">
            Así llevas control total de tu taller.
          </p>
        </div>
      </el-form>

      <div class="flex gap-2">
        <el-button v-if="step < 3" type="primary" size="large" class="flex-1 font-bold" :disabled="!isStepValid"
          :loading="step === 1 && isRealOrder && savingClient" @click="nextStep">
          {{ buttonLabel }}
        </el-button>
        <el-button v-else type="primary" size="large" class="flex-1 font-bold" :loading="loading" @click="submit">
          ✨ {{ isRealOrder ? 'Guardar orden real' : 'Guardar orden de práctica' }}
        </el-button>
      </div>

      <p v-if="step === 3" class="text-center text-xs text-gray-400 m-2 ">
        <i class="bx bxs-circle text-blue-500"></i> Tu orden está lista para guardarse
      </p>
    </div>

    <el-dialog :visible.sync="showCreateDeviceDialog" :width="'600px'" :close-on-click-modal="false" append-to-body
      :modal-append-to-body="true">
      <new-device-model-form @onCancel="showCreateDeviceDialog = false" @onSave="handleDeviceSaved" />
    </el-dialog>
  </el-dialog>
</template>

<script>
import error from '@/mixins/error';
import OrderService from '@/services/OrderService';
import CatalogService from '@/services/CatalogService';
import CategoryService from '@/services/CategoryService';
import ClientService from '@/services/ClientService';
import HttpProxy from '@/services/HttpProxy';
import { ContactTypeEnum } from '@/constants';
import NewDeviceModelForm from '@/views/app/orders/form/model/NewDeviceModelForm';
import CategoryDialogService, { command as CategoryDialogCommand, commands } from '@/components/dialogs/Category/service';
import CategoryEnum from '@/components/dialogs/Category/enum';
import { updateCompliance } from '@/utils/compliance';

export default {
  name: 'ThirdStepOnboarding',
  mixins: [error],
  components: {
    NewDeviceModelForm,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    width: {
      type: String,
      default: '500px',
    },
  },
  data() {
    return {
      isRealOrder: false,
      loading: false,
      step: 1,
      model: {
        clientName: '',
        clientPhone: '',
        device: null,
        problemDescription: '',
        service: null,
        price: '',
      },
      rules: {
        clientName: [
          { required: true, message: 'El nombre del cliente es requerido', trigger: 'blur' },
        ],
        device: [
          { required: true, message: 'El dispositivo es requerido', trigger: 'change' },
        ],
        problemDescription: [
          { required: true, message: 'La descripción del problema es requerida', trigger: 'blur' },
        ],
        service: [
          { required: true, message: 'El servicio es requerido', trigger: 'change' },
        ],
        price: [
          { required: true, message: 'El precio es requerido', trigger: 'blur' },
        ],
      },
      devices: [],
      services: [],
      deviceSearchLoading: false,
      serviceSearchLoading: false,
      savingClient: false,
      showCreateDeviceDialog: false,
      commandSubscription: null,
      realClientId: null,
    };
  },
  computed: {
    isStepValid() {
      if (this.step === 1) return this.model.clientName.trim().length > 0;
      if (this.step === 2) return this.model.device && this.model.service && this.model.problemDescription.trim().length > 0 && this.model.price;
      // Step 3 is always valid (summary review before submit)
      return true;
    },
    summaryDeviceName() {
      if (this.model.device && typeof this.model.device === 'object') {
        return `${this.model.device.brand} ${this.model.device.model}`;
      }
      return 'Dispositivo Demo';
    },
    summaryServiceName() {
      if (this.model.service && typeof this.model.service === 'object') {
        return this.model.service.name;
      }
      return 'Servicio Demo';
    },
    stepTitle() {
      if (this.step === 1) return 'Agrega tu primer cliente';
      if (this.step === 2) return 'Agrega tu primer servicio';
      return ''; // No step title for summary step
    },
    stepSubtitle() {
      if (this.step === 1) {
        return 'Puedes usar datos de ejemplo o registrar un cliente real.';
      }
      if (this.step === 2) {
        return 'Así llevarás el control de tu taller todos los días.';
      }
      return ''; // No subtitle for summary step
    },
    summaryClientName() {
      return this.model.clientName.trim() || 'Cliente Demo';
    },
    summaryClientPhone() {
      return this.model.clientPhone.trim() || 'Sin telefono';
    },
    buttonLabel() {
      if (this.step === 1) {
        return this.isRealOrder ? 'Continuar con orden real →' : 'Continuar con orden de práctica →';
      }
      if (this.step === 2) {
        return this.isRealOrder ? 'Guardar orden real →' : 'Guardar orden de práctica →';
      }
      return 'Continuar →';
    },

    visible: {
      get() {
        return this.show;
      },
      set(value) {
        if (!value) {
          this.skip();
        }
      },
    },
  },
  created() {
    this.commandSubscription = CategoryDialogCommand.subscribe(this.customCommands);
  },
  destroyed() {
    if (this.commandSubscription) {
      this.commandSubscription.unsubscribe();
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.step = 1;
        this.model = {
          clientName: '',
          clientPhone: '',
          device: null,
          problemDescription: '',
          service: null,
          price: '',
        };
        this.devices = [];
        this.services = [];
        this.savingClient = false;
        this.realClientId = null;
        this.showCreateDeviceDialog = false;
        this.$nextTick(() => {
          this.$refs.form?.clearValidate();
        });
      }
    },
  },
  methods: {
    async loadSuggestedDevices() {
      if (this.devices.length === 0) {
        this.deviceSearchLoading = true;
        try {
          const result = await CatalogService.getAllDeviceModels({ pageSize: 10 });
          this.devices = result?.data || [];
        } catch (e) {
          this.$toastr.e(this.getErrorMessage(e));
          this.devices = [];
        } finally {
          this.deviceSearchLoading = false;
        }
      }
    },
    async searchDevices(query) {
      if (!query) {
        this.devices = [];
        return;
      }
      this.deviceSearchLoading = true;
      try {
        const result = await CatalogService.getAllDeviceModels({ textSearch: query });
        this.devices = result?.data || [];
      } catch (e) {
        this.$toastr.e(this.getErrorMessage(e));
        this.devices = [];
      } finally {
        this.deviceSearchLoading = false;
      }
    },
    async searchServices(query) {
      if (!query) {
        this.services = [];
        return;
      }
      this.serviceSearchLoading = true;
      try {
        const result = await CategoryService.Service.get({ textSearch: query });
        this.services = this.normalizeServices(result || []);
      } catch (e) {
        this.$toastr.e(this.getErrorMessage(e));
        this.services = [];
      } finally {
        this.serviceSearchLoading = false;
      }
    },
    async loadSuggestedServices() {
      if (this.services.length === 0) {
        this.serviceSearchLoading = true;
        try {
          const result = await CategoryService.Service.get({});
          this.services = this.normalizeServices(result || []);
        } catch (e) {
          this.$toastr.e(this.getErrorMessage(e));
          this.services = [];
        } finally {
          this.serviceSearchLoading = false;
        }
      }
    },
    customCommands({ type, payload }) {
      switch (type) {
        case commands.create:
          this.handleServiceCreated(payload);
          break;
        default:
      }
    },
    showService() {
      CategoryDialogService.show({ type: CategoryEnum.Service });
    },
    handleServiceCreated(service) {
      if (service.type === CategoryEnum.Service) {
        // Agregar el servicio a la lista si no existe
        const existingService = this.services.find(s => s.id === service.id);
        if (!existingService) {
          service.__uid = service.id ? `s-${service.id}` : `s-noid-${Date.now()}`;
          this.services.unshift(service);
        }
        // Auto-seleccionar el servicio
        this.model.service = service;
      }
    },

    normalizeServices(list) {
      if (!Array.isArray(list)) return [];
      return list.map((s, idx) => ({
        ...s,
        __uid: s && s.id ? `s-${s.id}` : `s-noid-${idx}-${Date.now()}`,
      }));
    },
    async nextStep() {
      if (this.step === 1 && this.isRealOrder) {
        if (this.savingClient) {
          return;
        }

        this.savingClient = true;
        try {
          await this.ensureRealClient();
        } catch (e) {
          this.$toastr.e(this.resolveErrorMessage(e));
          return;
        } finally {
          this.savingClient = false;
        }
      }

      if (this.step < 3) this.step += 1;
    },
    prevStep() {
      if (this.step > 1) this.step -= 1;
    },
    async submit() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) {
          return;
        }

        if (this.loading) {
          return;
        }

        this.loading = true;
        try {
          let response = null;
          if (this.isRealOrder) {
            response = await this.createRealOrder();
            await updateCompliance(this, 'firstRealOrderCompletedAt');
          } else {
            response = await this.createDemoOrder();
            await updateCompliance(this, 'practiceOrderCompletedAt');
          }

          const successMessage = this.isRealOrder
            ? 'Orden creada exitosamente'
            : 'Orden demo creada exitosamente';
          this.$toastr.s(successMessage);

          const orderData = {
            clientName: this.model.clientName,
            device: this.summaryDeviceName,
            service: this.summaryServiceName,
            price: this.model.price || 0,
            isRealOrder: this.isRealOrder,
            response,
          };
          this.$emit('order-created', orderData);
        } catch (e) {
          this.$toastr.e(this.resolveErrorMessage(e));
        } finally {
          this.loading = false;
        }
      });
    },
    createDemoOrder() {
      const clientName = this.model.clientName || 'Cliente Demo';
      const clientPhone = this.model.clientPhone || '';
      const deviceId = this.model.device && this.model.device.id ? this.model.device.id : null;
      const deviceName = this.model.device && this.model.device.model
        ? `${this.model.device.brand} ${this.model.device.model}`
        : 'Dispositivo Demo';
      const serviceId = this.model.service && this.model.service.id ? this.model.service.id : null;
      const serviceName = this.model.service && this.model.service.name ? this.model.service.name : 'Servicio Demo';

      const { account } = this.$store.state;

      const payload = {
        accountId: account?.id,
        businessId: account?.businessId,
        clientName,
        clientPhone,
        deviceId,
        deviceName,
        problemDescription: this.model.problemDescription,
        serviceId,
        serviceName,
        price: this.model.price || 0,
      };

      return OrderService.createTestOrder(payload);
    },
    async createRealOrder() {
      const clientName = this.model.clientName || 'Primer Cliente';
      const clientPhone = this.model.clientPhone || '';
      const serviceId = this.model.service && this.model.service.id ? this.model.service.id : null;
      const price = parseFloat(this.model.price || 0);
      const serviceDescription = this.model.problemDescription || null;

      const { account, business } = this.$store.state;

      const clientId = this.realClientId || await this.ensureRealClient();
      const statusId = 1;
      const deviceModelId = this.model.device?.id || null;
      const deviceTypeId = this.model.device?.deviceType?.id || this.model.device?.deviceTypeId || null;
      const deviceModel = this.model.device ? {
        id: this.model.device.id,
        brand: this.model.device.brand || '',
        model: this.model.device.model || '',
      } : null;

      const payload = {
        accountId: account?.id,
        businessId: account?.businessId,
        businessInfoId: business?.id || account?.businessId,
        clientId,
        createdByAccountId: account?.id,
        priority: null,
        dueDate: null,
        subtotalAmount: price,
        totalAmount: price,
        status: statusId,
        clientName,
        clientPhone,
        orderDevices: this.model.device ? [{
          id: null,
          orderId: null,
          deviceType: null,
          deviceTypeId,
          deviceModel,
          deviceModelId,
          imei: null,
          deviceLockType: 1,
          passcode: null,
          pattern: null,
          hasDiagnostic: true,
          diagnosticNotes: this.model.problemDescription || null,
          hasWarranty: false,
          warrantyDays: 0,
          checklistItems: [],
          orderDeviceProducts: [],
          orderDeviceServices: serviceId ? [{
            serviceId,
            description: serviceDescription,
            cost: 0,
            price,
          }] : [],
        }] : [],
        orderProducts: [],
        orderTaxes: [],
        orderAdvances: [],
        orderProductsInventory: [],
        images: [],
        authorizedByClient: false,
        statusId,
      };

      return OrderService.create(payload);
    },
    buildClientPayload() {
      const { account, business } = this.$store.state;
      const normalizedClientPhone = this.model.clientPhone ? String(this.model.clientPhone).trim() : '';
      const phoneCountryCodeId = business?.phoneCountryCodeId || this.getDefaultPhoneCountryCodeId();

      return {
        fullName: this.model.clientName.trim(),
        phone: normalizedClientPhone,
        phoneCountryCodeId,
        email: null,
        contactType: ContactTypeEnum.Phone,
        contactDescription: null,
        sendEmailNotifications: false,
        dni: null,
        businessInfoId: business?.id || account?.businessId,
      };
    },
    async ensureRealClient() {
      if (!this.model.clientName || !this.model.clientName.trim()) {
        throw new Error('El nombre del cliente es requerido');
      }

      const resolvedPhoneCountryCodeId = await this.resolvePhoneCountryCodeId();
      const payload = {
        ...this.buildClientPayload(),
        phoneCountryCodeId: resolvedPhoneCountryCodeId,
      };

      if (!payload.businessInfoId) {
        throw new Error('No se pudo identificar el negocio para guardar el cliente');
      }

      if (!payload.phoneCountryCodeId) {
        throw new Error('No se encontró el código de país del negocio para guardar el cliente');
      }

      if (!payload.phone) {
        throw new Error('Para crear una orden real, captura el teléfono del cliente');
      }

      if (this.realClientId) {
        const updatedClient = await this.safeClientServiceCall(() => this.updateClient(this.realClientId, payload));
        return updatedClient?.id || this.realClientId;
      }

      const savedClient = await this.safeClientServiceCall(() => this.createClient(payload));
      const savedClientId = savedClient?.id;

      if (!savedClientId) {
        throw new Error('No fue posible guardar el cliente');
      }

      this.realClientId = savedClientId;
      return savedClientId;
    },
    async resolvePhoneCountryCodeId() {
      const businessPhoneCountryCodeId = this.$store.state?.business?.phoneCountryCodeId;
      if (businessPhoneCountryCodeId) {
        return businessPhoneCountryCodeId;
      }

      let defaultPhoneCountryCodeId = this.getDefaultPhoneCountryCodeId();
      if (defaultPhoneCountryCodeId) {
        return defaultPhoneCountryCodeId;
      }

      await CatalogService.getFlags();
      defaultPhoneCountryCodeId = this.getDefaultPhoneCountryCodeId();
      return defaultPhoneCountryCodeId;
    },
    getDefaultPhoneCountryCodeId() {
      const flags = this.$store.state?.catalogs?.flags || [];
      const mexicoFlag = flags.find((flag) => {
        const countryCode = String(flag?.countryCode || '').replace('+', '').trim();
        const isoCode = String(flag?.isoCode || '').toUpperCase();
        return countryCode === '52' || isoCode === 'MX';
      });
      return mexicoFlag?.id || null;
    },
    resolveErrorMessage(error) {
      return error?.message
        || error?.response?.data?.error?.message
        || error?.response?.data?.message
        || this.getErrorMessage(error);
    },
    async createClient(payload) {
      if (ClientService && typeof ClientService.create === 'function') {
        return ClientService.create(payload);
      }

      const response = await HttpProxy.submit({
        method: 'post',
        url: 'clients',
        data: payload,
      });
      return response?.data ?? null;
    },
    async updateClient(clientId, payload) {
      if (ClientService && typeof ClientService.update === 'function') {
        return ClientService.update(clientId, payload);
      }

      const response = await HttpProxy.submit({
        method: 'put',
        url: `clients/${clientId}`,
        data: payload,
      });
      return response?.data ?? null;
    },
    async safeClientServiceCall(request) {
      try {
        return await request();
      } catch (error) {
        throw new Error(this.resolveErrorMessage(error));
      }
    },
    handleDeviceSaved(device) {
      const existingDevice = this.devices.find(d => d.id === device.id);
      if (!existingDevice) {
        this.devices.unshift(device);
      }
      // Auto-seleccionar el dispositivo
      this.model.device = device;
      this.showCreateDeviceDialog = false;
    },
    skip() {
      this.$emit('skip');
    },
  },
};
</script>

<style scoped>
/deep/ .el-dialog__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.order-create-dialog.el-dialog {
  border-radius: 12px;
  margin: 0 !important;
  top: 0 !important;
}

.order-create-dialog .el-dialog__header {
  display: none;
}

.order-create-dialog .el-dialog__body {
  padding: 0;
}

.order-create-dialog .el-form-item {
  margin-bottom: 12px;
}

/deep/ .order-create-dialog .el-input {
  font-size: 14px;
}

/deep/ .order-create-dialog .el-textarea__inner {
  font-size: 14px;
  padding: 10px 12px;
  min-height: 120px;
}

/deep/ .order-create-dialog .el-select {
  width: 100%;
}

/deep/ .order-create-dialog .space-y-4>div {
  margin-bottom: 16px;
}

/deep/ .order-create-dialog .el-input__prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  height: 20px;
  width: 20px;
}

/deep/ .order-create-dialog .el-input__prefix i {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

/deep/ .order-create-dialog .el-input input {
  padding-left: 35px !important;
}

/deep/ .order-create-dialog .el-select .el-input__prefix {
  display: flex;
  align-items: center;
  justify-content: center;
}

/deep/ .el-dialog {
  z-index: 2500 !important;
}

/deep/ .el-dialog__wrapper {
  z-index: 2000 !important;
}

/* Solución para diálogos anidados */
/deep/ .el-dialog__wrapper {
  pointer-events: auto;
}

/* Hacer que el backdrop no bloquee los clicks en diálogos internos */
/deep/ .el-dialog__wrapper::before {
  display: none;
}

/* Asegurar que los diálogos mounted en body aparezcan encima */
/deep/ body>.el-dialog__wrapper:nth-child(n+2) {
  z-index: 3000 !important;
  background-color: rgba(0, 0, 0, 0) !important;
  pointer-events: none;
}

/deep/ body>.el-dialog__wrapper:nth-child(n+2) .el-dialog {
  z-index: 3100 !important;
  pointer-events: auto;
}

/* Último diálogo (más anidado) con fondo oscuro */
/deep/ body>.el-dialog__wrapper:last-child {
  background-color: rgba(0, 0, 0, 0.7) !important;
  pointer-events: auto;
  z-index: 3200 !important;
}

/deep/ body>.el-dialog__wrapper:last-child .el-dialog {
  z-index: 3300 !important;
}

/* Asegurar que message boxes estén encima */
/deep/ .el-message-box,
/deep/ .el-notification {
  z-index: 4000 !important;
}
</style>
